import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT } from "@/data/site";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-[#1F2421]/15 bg-white px-4 py-3 text-sm text-[#1F2421] placeholder:text-[#7A8278]/60 focus:outline-none focus:ring-2 focus:ring-[#4A5D3E]/40 focus:border-[#4A5D3E] transition";

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contactPage;
  const [params] = useSearchParams();
  const presetRoom = params.get("room");

  const [form, setForm] = useState({
    name: "", email: "", phone: "", arrival: "", departure: "", guests: 2,
    message: "", room: presetRoom || "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const payload = { ...form, guests: Number(form.guests), language: lang, room: form.room || null };
    try {
      await axios.post(`${API}/inquiries`, payload);
      try {
        const saved = JSON.parse(localStorage.getItem("bevanda-inquiries") || "[]");
        saved.push({ ...payload, at: new Date().toISOString() });
        localStorage.setItem("bevanda-inquiries", JSON.stringify(saved));
      } catch {}
      setStatus("sent");
      toast.success(c.toastOk);
    } catch {
      setStatus("idle");
      toast.error(c.toastErr);
    }
  };

  const waUrl = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(c.waMessage)}`;

  return (
    <div data-testid="contact-page" key={lang} className="pt-16 sm:pt-20">
      <Seo title={t.seo.contact.title} description={t.seo.contact.desc} image="/images/hero.jpg" />

      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-14 sm:pt-20 pb-12">
        <Reveal>
          <Eyebrow>Sobe Bevanda — Međugorje</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F2421]">{c.title}</h1>
          <p className="text-lg sm:text-xl text-[#4A504B] font-light mt-6 max-w-2xl">{c.lead}</p>
        </Reveal>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-10">
          <Reveal>
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#7A8278] mb-1.5">{c.addressTitle}</p>
                <p className="flex items-start gap-2.5 text-[#1F2421]">
                  <MapPin size={17} className="text-[#4A5D3E] mt-0.5 shrink-0" />
                  <span>Sobe Bevanda<br />{CONTACT.address}</span>
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#7A8278] mb-1.5">{c.phoneTitle}</p>
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-2.5 text-[#1F2421] hover:text-[#4A5D3E] transition-colors">
                  <Phone size={17} className="text-[#4A5D3E] shrink-0" /> {CONTACT.phoneDisplay}
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#7A8278] mb-1.5">{c.emailTitle}</p>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-2.5 text-[#1F2421] hover:text-[#4A5D3E] transition-colors">
                  <Mail size={17} className="text-[#4A5D3E] shrink-0" /> {CONTACT.email}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="text-xs uppercase tracking-[0.22em] text-[#7A8278] mb-4">{c.directTitle}</p>
            <div className="flex flex-wrap gap-3">
              <a href={CONTACT.phoneHref} data-testid="contact-call-button" className="inline-flex items-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-5 py-2.5 transition-colors">
                <Phone size={15} /> {t.cta.call}
              </a>
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="inline-flex items-center gap-2 rounded-full border border-[#4A5D3E]/40 text-[#4A5D3E] text-sm font-semibold px-5 py-2.5 hover:bg-[#E8EFE6] transition-colors">
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-button" className="inline-flex items-center gap-2 rounded-full border border-[#1F2421]/15 text-[#1F2421] text-sm font-semibold px-5 py-2.5 hover:bg-[#F3EFE6] transition-colors">
                <Mail size={15} /> {t.cta.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-2xl border-8 border-white shadow-lg">
              <iframe
                title="Sobe Bevanda — Međugorje map"
                src={CONTACT.mapEmbed}
                className="w-full h-64"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="bg-white rounded-2xl border border-[#1F2421]/8 p-6 sm:p-10 shadow-[0_20px_60px_-30px_rgba(31,36,33,0.2)]">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1F2421] mb-8">{c.formTitle}</h2>

            {status === "sent" ? (
              <div data-testid="inquiry-success" className="text-center py-12">
                <CheckCircle2 size={52} strokeWidth={1.5} className="text-[#4A5D3E] mx-auto" />
                <h3 className="font-serif-display text-2xl text-[#1F2421] mt-6">{c.successTitle}</h3>
                <p className="text-[#4A504B] mt-3 max-w-md mx-auto">{c.successText}</p>
                <button
                  data-testid="send-another-inquiry"
                  onClick={() => { setStatus("idle"); setForm({ name: "", email: "", phone: "", arrival: "", departure: "", guests: 2, message: "", room: "" }); }}
                  className="mt-8 text-sm font-semibold text-[#4A5D3E] hover:text-[#3A4A31] transition-colors"
                >
                  {c.sendAnother}
                </button>
              </div>
            ) : (
              <form data-testid="inquiry-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="f-name" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.name} *</label>
                  <input id="f-name" data-testid="inquiry-name" required value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-email" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.email} *</label>
                  <input id="f-email" data-testid="inquiry-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-phone" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.phone} *</label>
                  <input id="f-phone" data-testid="inquiry-phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-guests" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.guests} *</label>
                  <input id="f-guests" data-testid="inquiry-guests" type="number" min="1" max="30" required value={form.guests} onChange={set("guests")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-arrival" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.arrival} *</label>
                  <input id="f-arrival" data-testid="inquiry-arrival" type="date" required value={form.arrival} onChange={set("arrival")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="f-departure" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.departure} *</label>
                  <input id="f-departure" data-testid="inquiry-departure" type="date" required value={form.departure} onChange={set("departure")} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="f-room" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.room}</label>
                  <select
                    id="f-room"
                    data-testid="inquiry-room"
                    value={form.room}
                    onChange={set("room")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="">—</option>
                    {t.roomsPage.items.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="f-message" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4A504B] mb-2">{c.fields.message}</label>
                  <textarea id="f-message" data-testid="inquiry-message" rows="4" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="inquiry-submit-button"
                    disabled={status === "sending"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] disabled:opacity-60 text-[#F9F8F3] text-sm font-semibold px-8 py-3.5 transition-colors duration-300"
                  >
                    {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                    {t.cta.send}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
